import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import moment from 'moment';

import { BASENAME, CHARGE_KIND, CHARGE_KIND_DISPLAY, LAYOUT_TYPES } from '../../../constants';
import LineTitleGray from '../../atoms/titles/LineTitleGray';
import LineTitle from '../../atoms/titles/LineTitle';
import UserInfoTable from '../../organisms/UserInfoTable';
import SubscriptionNoteTable from '../../molecules/SubscriptionNoteTable';
import RadioCash from '../../atoms/form/RadioCash';
import RadioCampaign from '../../atoms/form/RadioCampaign';
import Textlink from '../../atoms/link/Textlink';
import LinkButton from '../../atoms/button/LinkButton';
import CheckBox from '../../atoms/form/CheckBox';
import RadioGift from '../../atoms/form/RadioGift';
import GiftCardInput from '../../molecules/GiftCardInput';
import UserInfoPlanContents from '../../organisms/UserInfoPlanContents';
import { setCurrentStep, setLayoutType } from '../../../modules/app';
import environments  from '../../../environments';
import { RELATIONSHIP_OPTIONS, COUPON_TYPES, ENABLE_GIFT_CARD_FEATURE }  from '../../../constants';
import { getPlanFromSessionPlansByDate } from '../../../utils/plans';
import CustomerInfoDisplay from '../../molecules/CustomerInfoDisplay';
import { FetchCoupon } from '../../../queries/index.graphql';
import { makeSessionCoupon } from '../../../utils/input';
import { fetchSession, updateSession, useGiftCards, useTimeout } from '../../../modules/session';
import Loading from '../../atoms/Loading';
import { getCouponInfoFromCookie } from '../../../utils/cookie';
import { FDataFromSession } from '../../Head';
import ModalCampaignCaption from '../../modal/ModalCampaignCaption';
import ModalGiftCardCaption from '../../modal/ModalGiftCardCaption';
import ModalConfirmFreeReservation from '../../modal/ModalConfrimFreeReservation';
import FinalPrice from '../../molecules/FinalPrice';
import TimeoutErrorModal from '../../atoms/modal/TimeoutErrorModal';
import { getIsAna2 } from '../../../utils/Ana2';

const getEmergencyRelationshipDisplay = (relationship) => {
  if (!relationship) {
    return;
  }

  const option = RELATIONSHIP_OPTIONS.find((o) => o.value === relationship);
  return option ? option.name : '';
};

const FinalConfirmation = ({ session: initialSession }) => {
  const dispatch = useDispatch();
  const currentStep = 4;

  const [session, setSession] = useState(initialSession);
  const [coupon, setCoupon] = useState(initialSession.coupon);
  const [agreed, setAgreed] = useState(false);
  const [showTripCondition, setShowTripCondition] = useState(true);
  const [openCampaignModal, setOpenCampaignModal] = useState(false);
  const [openGiftCardModal, setOpenGiftCardModal] = useState(false);
  const [openConfirmFreeModal, setOpenConfirmFreeModal] = useState(false);
  const giftCardEnterForm = useRef(null);
  const reserveForm = useRef(null);

  useEffect(() => {
    dispatch(
      setLayoutType(LAYOUT_TYPES.step)
    );
    dispatch(setCurrentStep(currentStep));

    return () => {
      dispatch(
        setLayoutType(LAYOUT_TYPES.default)
      );
    };
  }, []);

  const { isTimeoutError } = useTimeout();

  const { siteId, discountCode, transactionId } = getCouponInfoFromCookie();
  const { data, loading, error } = useQuery(
    FetchCoupon,
    {
      variables: { sessionID: initialSession.id, siteID: siteId, discountCode, transactionId },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  );

  if (!isTimeoutError && error && error.graphQLErrors && error.graphQLErrors.length) {
    const firstError = error.graphQLErrors[0];
    const redirectUrl = firstError.extensions && firstError.extensions.redirect;
    window.open(redirectUrl, '_self');
  }

  const coupons = (data && data.coupon) || [];

  // https://gitlab.fqsv.net/dp-plus/front/issues/165#note_29470
  const refetchSession = async () => {
    if (isTimeoutError) {
      return;
    }

    const session = await fetchSession();
    setSession(session);
  };

  useEffect(() => {
    if (!loading) {
      refetchSession();
    }
  }, [loading]);

  const {
    id: sessionId, flightSchedule, plans, member, accompanies,
    office, people, giftCard: initialGiftCardInfo,
  } = session;

  const {
    enabledGiftCard, onChangeEnabledGiftCard, giftCards, giftCardInputTotalNumber, isEnteredByUser,
    onChangeGiftCardInputAmount, onClickGiftLink, giftCardEnterUrl, clearGiftCardInputAmount,
  } = useGiftCards(dispatch, initialGiftCardInfo, giftCardEnterForm);

  const handleCouponChange = (coupon) => {
    setCoupon(coupon);

    const sessionCoupon = makeSessionCoupon(coupon);
    dispatch(updateSession(sessionCoupon));

    const isEnteredGiftAmonut = isEnteredByUser && giftCardInputTotalNumber > 0;
    if (isEnteredGiftAmonut) {
      clearGiftCardInputAmount();
      alert('請求金額が変更されました。ギフトカード利用金額を入力しなおしてください。');
    }
  };

  const infantNum = session.people.infant || 0;
  const childrenCNum = session.people.childrenC || 0;
  const children2Num = session.people.children2 || 0;
  const haveAnyInfant = infantNum > 0 || childrenCNum > 0 || children2Num > 0;

  const handleBack = () => {
    window.open(`${BASENAME}/entry_customer`, '_self');
  };

  const actionPageUrl = `${environments.API_BASE_URI}/reserve`;

  const outboundFlight = flightSchedule.departure;
  const inboundFlight = flightSchedule.arrival;

  const discount = coupon ? coupon.totalDiscount || 0 : 0;

  const totalPrice = session.totalAmount;

  const finalPrice = totalPrice - discount;

  useEffect(() => {
    const _validCoupons = filterValidCoupons(coupons, totalPrice);
    if (hasDiscount(_validCoupons)) {
      const discount = _validCoupons.find((c) => c && c.type === COUPON_TYPES.discount);
      handleCouponChange(discount);
    }
  }, [coupons]);

  const validCoupons = filterValidCoupons(coupons, totalPrice);

  const tripStartDayString = outboundFlight.departureTime.substr(0, 10);
  const tripStartDate = moment(tripStartDayString);
  const tripEndDayString = inboundFlight.arrivalTime.substr(0, 10);
  const tripEndDate = moment(tripEndDayString);
  const tripTotalDays = tripEndDate.diff(tripStartDate, 'days') + 1;

  const schedule = new Array(tripTotalDays).fill(undefined).map((_, i) => {
    const date = tripStartDate.clone().add(i, 'days');

    const plan = getPlanFromSessionPlansByDate(plans, date);

    return {
      date,
      plan,
    };
  });

  const todayDisplay =  moment().format('YYYY年M月D日(ddd)');

  const { name, note, lifeSupportNumber, emergencyContact, phoneticName } = member;
  const emergencyNameDisplay = emergencyContact ? emergencyContact.name : '';
  const emergencyRelationshipDisplay = emergencyContact ? getEmergencyRelationshipDisplay(emergencyContact.relationship) : '';
  const emergencyPhoneNumberDisplay = emergencyContact && emergencyContact.tel ?
    `${emergencyContact.tel.first || ''} - ${emergencyContact.tel.second || ''} - ${emergencyContact.tel.third || ''}` : '';

  const tripConditionUrl = 'http://rsv.nta.co.jp/F_Booking/static/ryokojoken_shusai.htm';
  const giftCardTermLink = 'https://www.nta.co.jp/nrs/images/gift/gift.pdf';
  const dreamCardTermLink = 'https://www.nta.co.jp/nrs/dreamplan/dreamplan.pdf';

  const termsConditionLink = `${environments.API_BASE_URI}/terms-condition?s=${sessionId}`;
  const onClickTermsCondition = () => window.open(termsConditionLink, '_blank');

  const isCommunitySupport = coupon && coupon.goto && coupon.goto.communitySupport;

  const validGiftCardInputTotalNumber = enabledGiftCard ? giftCardInputTotalNumber : 0;
  const billingPrice = finalPrice - validGiftCardInputTotalNumber;

  const makePaymentMethodDisplay = (giftCardInputTotalNumber) => {
    let paymentMethodDisplay = 'クレジットカード';
    if (!(enabledGiftCard && giftCardInputTotalNumber > 0)) {
      return paymentMethodDisplay;
    }

    if (finalPrice == giftCardInputTotalNumber) {
      paymentMethodDisplay = '日本旅行ギフトカード・ドリーム旅行カード';
      return paymentMethodDisplay;
    }

    paymentMethodDisplay = '日本旅行ギフトカード・ドリーム旅行カード（クレジットカード併用可能）';
    return paymentMethodDisplay;
  };

  const isFullPaidWithGiftCard = enabledGiftCard && finalPrice == giftCardInputTotalNumber;

  const makeNextPageLabel = () => {
    if (isFullPaidWithGiftCard) {
      return '予約を完了する';
    }

    return 'クレジットカード情報入力へ進む';
  };

  const confirmReservationWithGiftCard = () => {
    setOpenConfirmFreeModal(true);
  };

  const cancelReservationWithGiftCard = () => {
    setOpenConfirmFreeModal(false);
  };

  const doReserve = () => {
    reserveForm.current && reserveForm.current.submit();
  };

  const handleClickReserve = () => {
    if (isFullPaidWithGiftCard) {
      confirmReservationWithGiftCard();
      return;
    }

    doReserve();
  };

  const SelectedOutboundFlight = flightSchedule.departure;
  const SelectedOutboundDate = SelectedOutboundFlight.departureTime.substr(0, 10);
  const isAna2 = getIsAna2(SelectedOutboundFlight.airline, SelectedOutboundDate);

  return (
    <div className="pageEntryConfirmation">
      <FDataFromSession session={session} step={currentStep} />
      <Loading show={!isTimeoutError && loading} hideProgressBar progressRate={100} />
      <div className="EntryConfirmationBlock">
        <div className="EntryConfirmationBlockTitle">
          <LineTitleGray>申込み内容の最終確認</LineTitleGray>
        </div>
        <div className="EntryConfirmationBlockTable">
          <UserInfoTable
            tripTotalDays={tripTotalDays}
            schedule={schedule}
            plans={plans}
            outboundSessionFlight={outboundFlight}
            inboundSessionFlight={inboundFlight}
          />
        </div>
        <div className="EntryConfirmationPlanContents">
          <UserInfoPlanContents
            tripTotalDays={tripTotalDays}
            schedule={schedule}
            plans={plans}
            outboundSessionFlight={outboundFlight}
            inboundSessionFlight={inboundFlight}
          />
        </div>
      </div>
      <div className="EntryConfirmationBlock">
        <LineTitleGray>お客様情報確認</LineTitleGray>
        <div className="EntryConfirmationItem">
          <LineTitle text="代表者情報" />
          <CustomerInfoDisplay
            customer={member}
            representative
            isAna2={isAna2}
          />
        </div>
        <div className="EntryConfirmationItem">
          <LineTitle text="緊急連絡先" />
          <div className="userInfoItem">
            <div className="userInfoItemName">お名前</div>
            <div className="userInfoItemDetail">{emergencyNameDisplay}</div>
          </div>
          <div className="userInfoItem">
            <div className="userInfoItemName">続柄</div>
            <div className="userInfoItemDetail">{emergencyRelationshipDisplay}</div>
          </div>
          <div className="userInfoItem">
            <div className="userInfoItemName">電話番号</div>
            <div className="userInfoItemDetail">{emergencyPhoneNumberDisplay}</div>
          </div>
        </div>
        <div className="EntryConfirmationItem">
          <LineTitle text="同行者情報" />
          {accompanies.map((a, index) => {
            const { chargeKind } = a;
            const titleIndex = index + 1;
            const titleLabel = `同行者${titleIndex} ${CHARGE_KIND_DISPLAY[chargeKind]}`;

            return (
              <React.Fragment key={index}>
                <div className="wrapperLabel">{titleLabel}</div>
                <CustomerInfoDisplay
                  customer={a}
                  isAna2={isAna2}
                />
              </React.Fragment>
            );
          })}
        </div>
        <div className="EntryConfirmationItem">
          <LineTitle text="部屋割り" />
          <div className="userInfoItem">
            <div className="userInfoItemName">代表者</div>
            <div className="userInfoItemName phoneticName">{phoneticName.last} {phoneticName.first}</div>
            <div className="userInfoItemName">1部屋目</div>
          </div>
          {accompanies.map((a, index) => {
            const { name, chargeKind, roomAllocation } = a;
            let titleIndex = index + 1;
            const titleLabel = `同行者${titleIndex}`;

            if (chargeKind === CHARGE_KIND.infant || chargeKind === CHARGE_KIND.childrenC || chargeKind === CHARGE_KIND.children2) {
              return null;
            }

            if (chargeKind === CHARGE_KIND.childrenA) {
              titleIndex -= people.adult - 1;
            } else if (chargeKind === CHARGE_KIND.childrenB) {
              titleIndex -= (people.adult - 1 + people.childrenA || 0);
            }

            return (
              <div className="userInfoItem" key={index}>
                <div className="userInfoItemName">{titleLabel}</div>
                <div className="userInfoItemName phoneticName">{name.last} {name.first}</div>
                <div className="userInfoItemName">{roomAllocation}部屋目</div>
              </div>
            );
          })}
          {haveAnyInfant && (
            <div className="userInfoItem noteTextRed" >※こども(0～5歳)の寝具なし（添い寝）のお子様は部屋割りには含まれません。</div>
          )}
        </div>
        <div className="EntryConfirmationItem">
          <LineTitle text="要望・連絡事項" />
          <div className="userInfoItem">{note}</div>
        </div>
        {lifeSupportNumber && (
          <div className="EntryConfirmationItem">
            <LineTitle text="ライフサポート倶楽部会員番号" />
            <div className="userInfoItem">{lifeSupportNumber}</div>
          </div>
        )}
      </div>
      <div className="EntryConfirmationBlock">
        <LineTitleGray>お支払い方法選択</LineTitleGray>
        <div className="userInfoCash">
          <div className="">
            <div className="userInfoCashItemCampaignTitle">
              お客様がご利用可能な割引・キャンペーン
              <div
                className="iconWrapper"
                onClick={() => setOpenCampaignModal(true)}
              >
                <img
                  className="questionIcon"
                  src="/dpp/img/icons/question.png"
                />
              </div>
            </div>
            <ModalCampaignCaption open={openCampaignModal} onClose={() => setOpenCampaignModal(false)}/>
            <div className="userInfoCashItemCampaignRadio">
              <RadioCampaign
                coupons={validCoupons}
                onChange={handleCouponChange}
                selectedCoupon={coupon}
                hideNoSelected={hasDiscount(validCoupons)}
              />
            </div>
          </div>
          {ENABLE_GIFT_CARD_FEATURE && (
            <React.Fragment>
              <div className="userInfoCashItemCampaign">
                <div className="userInfoCashItemCampaignTitle">
                  日本旅行ギフトカード・ドリーム旅行カード
                  <div
                    className="iconWrapper"
                    onClick={() => setOpenGiftCardModal(true)}
                  >
                    <img
                      className="questionIcon"
                      src="/dpp/img/icons/question.png"
                    />
                  </div>
                </div>
                <ModalGiftCardCaption open={openGiftCardModal} onClose={() => setOpenGiftCardModal(false)}/>
                <div className="userInfoCashItemCampaignRadio">
                  <RadioGift
                    enabledGiftCard={enabledGiftCard}
                    onChangeEnabledGiftCard={onChangeEnabledGiftCard}
                  />
                </div>
                {enabledGiftCard && (
                  <React.Fragment>
                    <div className="userInfoCashItemGiftDesc">
                      <Textlink to={giftCardTermLink} outgoing>ギフトカード[PDF形式]</Textlink>&nbsp;
                      <Textlink to={dreamCardTermLink} outgoing>ドリーム旅行カード[PDF形式]</Textlink>&nbsp;のご利用についてご一読・同意の上、「カード番号を入力する」ボタンを押してください。
                    </div>
                    <div className="userInfoCashItemGiftDesc">
                      <LinkButton onClick={onClickGiftLink}>
                        <div className="userInfoCashItemGiftInputLink">
                          <img src="/dpp/img/icons/icon_checked.png" alt="" style={{ height: 20, width: 20 }} />
                          同意の上、カード番号を入力する<br />
                          <span className="userInfoCashItemGiftInputLinkNote">（日本旅行ギフトカード・ドリーム旅行カード）</span>
                        </div>
                      </LinkButton>
                    </div>
                    <form action={giftCardEnterUrl} method="POST" ref={giftCardEnterForm} >
                      <input hidden name="s" value={sessionId} readOnly />
                    </form>
                  </React.Fragment>
                )}
              </div>
              {enabledGiftCard && (
                <div className="giftCardList">
                  {giftCards && giftCards.map((g, i) => (
                    <GiftCardInput
                      key={g.giftCardKey}
                      number={i+1}
                      availableAmount={g.balance}
                      inputAmount={g.inputAmount}
                      onChangeInputAmount={(input) => onChangeGiftCardInputAmount(g.giftCardKey, input)}
                      giftCardNumber={g.giftCardNumber}
                      expirationDate={g.expirationDate}
                      finalPriceNumber={finalPrice}
                      giftCardInputTotalNumber={giftCardInputTotalNumber}
                    />
                  ))}
                </div>
              )}
            </React.Fragment>
          )}
          <div className="userInfoCashItem userInfoCashItemCampaign">
            <div className="userInfoCashItemTitle">お支払い方法</div>
            <div className="userInfoCashItemRadio">
              <RadioCash optionName={makePaymentMethodDisplay(giftCardInputTotalNumber)} />
            </div>
          </div>
        </div>
      </div>
      <FinalPrice
        className="finalPrice"
        totalPrice={totalPrice}
        discount={discount}
        finalPrice={finalPrice}
        giftCardInputTotal={giftCardInputTotalNumber}
        showGiftCardInputTotal={enabledGiftCard}
        billingPrice={billingPrice}
      />
      {!!(coupon && coupon.goto && coupon.goto.regionalCoupon) && (
        <div className="EntryConfirmationBlock">
          <LineTitleGray>
            {isCommunitySupport ? 'この予約で付与される地域クーポン' : 'この予約で付与される地域クーポン'}
          </LineTitleGray>
          <div className="EntryConfirmationItem">
            <div className="regionalCoupon">
              <div className="regionalCouponTitle">地域クーポン</div>
              <div className="regionalCouponValue">
                {`${(coupon.goto.regionalCoupon || 0).toLocaleString()}円分`}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="EntryConfirmationBlock">
        <LineTitleGray>申し込み注意事項</LineTitleGray>
        <div className="userInfoNote">
          <LineTitle text="取消規定" />
          <div className="noteItemBody">
            <p className="noteText noteTextStrong">
            取消料発生日：
              <span className="noteTextRed">{todayDisplay}</span>から取り消し料がかかります。
            </p>
            <div className="noteTable noteTablePC">
              <SubscriptionNoteTable />
            </div>
            <div className="noteTable noteTableSP">
              <SubscriptionNoteTable />
            </div>
            <div className="noteText">
              <div className="mb-5">予約完了後、お客様のご都合で予約取り消しを行った場合、即時で取消料一覧に定めた取消料をいただきます。</div>
              詳しい条件などは<Textlink to={tripConditionUrl} outgoing>こちら</Textlink>をご確認ください。
            </div>
          </div>
        </div>
        <div className="EntryConfirmationItem">
          <div className="noteItemTable">
            <div className="noteItemBody">
              <div className="noteTextStrong">ご予約後の変更について</div>
              <div>現予約を取消後、新規にご予約のお取り直しをお願いいたします。取消には所定の取消料が必要です。お申込み期限を切った場合は再予約もできません。また、旅行者氏名・年齢の訂正もできません。新たにご予約のお取り直しをお願いいたします。</div>
            </div>
            <div className="noteItemBody">
              <div className="noteTextRed noteText noteTextStrong">お子様（こども）について</div>
              <div className="noteTextRed">
                6歳以上のお子様は航空座席のみ（添い寝）のご予約はお取扱はできません。<br />
                こども(0～5歳)のお子様は「航空座席・寝具・食事なし」の予約が可能となります。<br />
                ※ただし、2026/05/19以降のANA便利用はこども(2歳)のお子様は「航空座席あり、寝具・食事なし」となります。
              </div>
            </div>
          </div>
        </div>
        <div className="userInfoNote">
          <LineTitle text="特別な配慮を必要とする方のお申込みについて" />
          <p className="conditionItemText">
            お客様の状況によっては、当初の手配内容に含まれていない特別な配慮、措置が必要になる可能性があります。 特別な配慮、措置が必要になる可能性のある方は、ご相談させていただきますので、取り扱い店舗に必ずお申し出ください。
          </p>
        </div>
        <div className="userInfoNote">
          <LineTitle text="未成年の方のお申込みについて" />
          <div className="conditionItemText mb-10">
            <ul className="pl-20">
              <li className="listItem">未成年者の旅行契約には、親権者の同意が必要です。</li>
              <li className="listItem">
                未成年者の方のみでのご参加の場合や未成年者の方に親権者以外の方が同行される場合には、同意書のご提出をお願いいたします。<br />
                なお、15歳未満の方のご参加は、原則として親権者（保護者等）のご同行が必要となります。<br />
                ※ここで示す年齢はご旅行契約時（予約時）のものとなります。
              </li>
            </ul>
          </div>
          <Textlink className="childLink" to="https://www.nta.co.jp/help/pdf/kokunaidoisyo.pdf" outgoing>【未成年のお客様のご旅行参加同意書】</Textlink>
        </div>
        <div className="userInfoNote">
          <LineTitle text="取引条件説明書" />
          <div className="conditionItemBody">
            <div className="conditionItemBodyText">
              <div className="conditionItemBodyTextMain">取引条件説明書を表示し表示された内容を保存または印刷してください。</div>
              <div className="conditionItemBodyTextSub">※取引条件説明を別画面で表示いたします。</div>
            </div>
            <div className="conditionItemBodyButton">
              <LinkButton onClick={onClickTermsCondition}>取引条件説明書を表示</LinkButton>
            </div>
          </div>
        </div>
        <div className="userInfoNote">
          <LineTitle text="旅行条件説明書" className="relative">
            <div className="rightItem clickable" onClick={() => setShowTripCondition(!showTripCondition)}>
              <div className={`icon ${showTripCondition ? '' : 'plus'}`}>
                <span className="iconMark" />
              </div>
            </div>
          </LineTitle>
          {showTripCondition && (
            <iframe
              src="https://rsv.nta.co.jp/F_Booking/static/ryokojoken_shusai.htm"
              style={{ marginTop: 24, width: '100%', height: 200, overflow: 'auto' }}
            />
          )}
          <p className="conditionItemText">
          ・取引条件説明書類を保存または印刷し、内容をご確認ください。
            <br />・ご旅行申込みの際は、上記旅行条件書及びコース詳細ページ記載内容等を必ずご確認ください。
            <br />・個人情報の取り扱いについては、旅行条件書内の『22. 個人情報の取扱について』をご確認ください。
          </p>
        </div>
        <div className="userInfoNote">
          <div className="entryCheck">
            <CheckBox name="entryCheck" id="entryCheck" checked={agreed} onChange={() => setAgreed(!agreed)} disabled={isTimeoutError}>
                上記のお客様情報に誤りがないこと・お申込み注意事項（取消・変更・お子様（こども）について）・取引条件説明書・国内募集型企画旅行条件書を確認・同意しました
            </CheckBox>
          </div>
          <div className="subscriptionCheckActions">
            <div className="linkActions">
              <div className="linkClose">
                <LinkButton buttonType="navigate" onClick={handleBack}>戻る</LinkButton>
              </div>
              {!isTimeoutError && (
                <div className="linkMypage">
                  <LinkButton buttonType="cta" disabled={!agreed} onClick={handleClickReserve}>{makeNextPageLabel()}</LinkButton>
                  <form ref={reserveForm} action={actionPageUrl} method="POST">
                    <input hidden name="s" value={sessionId} readOnly />
                  </form>
                  <ModalConfirmFreeReservation
                    open={openConfirmFreeModal}
                    onClose={cancelReservationWithGiftCard}
                    onReserve={doReserve}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="EntryConfirmationBlock">
        <LineTitleGray>取扱店舗</LineTitleGray>
        <div className="EntryConfirmationItem">
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">店舗名</div>
            <div className="userInfoItemDetail">{office && office.name}</div>
          </div>
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">住所</div>
            <div className="userInfoItemDetail">
              {`〒${(office && office.postalCode) || ''} ${(office && office.address) || ''}`}
            </div>
          </div>
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">電話番号</div>
            <div className="userInfoItemDetail">{office && office.tel}</div>
          </div>
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">営業時間</div>
            <div
              className="userInfoItemDetail"
              dangerouslySetInnerHTML={{ __html: office && office.businessHours }}
            />
          </div>
          <div className="userInfoItem hasBorderLine">
            <div className="userInfoItemName">休業日</div>
            <div className="userInfoItemDetail">{office && office.holiday}</div>
          </div>
          {office && office.businessComment && (
            <div className="userInfoItem hasBorderLine">
              <div className="userInfoItemDetail">{office && office.businessComment}</div>
            </div>
          )}
          <div className="userInfoItem">
            <div className="userInfoItemName hasBorderLine">総合旅行業務取扱管理者</div>
            <div className="userInfoItemDetail">
              <div className="mainText">{office && office.managerName}</div>
            </div>
          </div>
          <div className="subText">
            旅行業務取扱管理者とは、お客様の旅行を取り扱う支店(営業所)での取引に関する責任者です。 この旅行契約に関し、担当者からの説明に不明な点があれば、ご遠慮なく取扱管理者にお尋ねください。
          </div>
        </div>
      </div>
      {isTimeoutError && <TimeoutErrorModal />}
    </div>
  );
};

FinalConfirmation.propTypes = {
  session: PropTypes.object,
};

export default FinalConfirmation;

const hasDiscount = (coupons) => !!coupons.find((c) => c && c.type === COUPON_TYPES.discount);

const filterValidCoupons = (coupons, totalPrice) => coupons.filter((c) => c.totalDiscount <= totalPrice);
